import React from "react";

import { Clock, EarthLock } from "lucide-react";
import moment from "moment";
import { renderToStaticMarkup } from "react-dom/server";

import TooltipLines from "@/src/components/tooltip/TooltipLines";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";
import { isGeoblockActive } from "@/src/utils/geoblock";

import DataTableColumnHeader from "../dataTable/DataTableColumnHeader";
import { CELL_ICON_CLASS } from "../dataTable/dataTableStyles";

import type { RowData } from "@tanstack/react-table";
import type { TFunction } from "i18next";

import type {
  ColumnLayout,
  DataTableColumnDef,
} from "../dataTable/dataTable.types";

const RESTRICTION_DATE_FORMAT = "LL";

interface RestrictionRow {
  temporal_restriction?: boolean;
  geoblock_mode?: string;
  begin_date?: string | null;
  end_date?: string | null;
}

interface RestrictionsColumnOptions<TData> {
  t: TFunction;
  layout?: ColumnLayout;
  showsTemporalTooltip?: boolean;
  getTooltipLineList?: (row: TData) => string[];
}

const DEFAULT_LAYOUT: ColumnLayout = {
  width: "10%",
  minWidth: 100,
  align: "center",
};

function getTemporalTooltip(row: RestrictionRow, t: TFunction): string {
  const startAt = moment(row.begin_date).format(RESTRICTION_DATE_FORMAT);
  if (!row.end_date) {
    return t("common:available_start_at", { date: startAt });
  }
  return t("common:available_start_end", {
    start_at: startAt,
    end_at: moment(row.end_date).format(RESTRICTION_DATE_FORMAT),
  });
}

export function restrictionsColumn<TData extends RowData & RestrictionRow>({
  t,
  layout,
  showsTemporalTooltip = false,
  getTooltipLineList,
}: RestrictionsColumnOptions<TData>): DataTableColumnDef<TData> {
  const temporalLabel = t("editor-content:restrictionTemporal");
  const geoblockLabel = t("editor-content:restrictionGeoblock");

  return {
    id: "restrictions",
    accessorFn: (row) => [
      ...(row.temporal_restriction ? ["temporal_restriction"] : []),
      ...(isGeoblockActive(row.geoblock_mode) ? ["geoblock_mode"] : []),
    ],
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t("restrictions")} />
    ),
    enableSorting: false,
    meta: {
      ...DEFAULT_LAYOUT,
      ...layout,
      label: t("restrictions"),
      filterOptions: [
        {
          value: "temporal_restriction",
          label: temporalLabel,
          icon: <Clock />,
        },
        {
          value: "geoblock_mode",
          label: geoblockLabel,
          icon: <EarthLock />,
        },
      ],
    },
    cell: ({ row }) => {
      const isTemporal = Boolean(row.original.temporal_restriction);
      const isGeoblocked = isGeoblockActive(row.original.geoblock_mode);
      if (!isTemporal && !isGeoblocked) return null;

      const tooltipLineList = [
        ...(showsTemporalTooltip && isTemporal
          ? [getTemporalTooltip(row.original, t)]
          : []),
        ...(getTooltipLineList?.(row.original) ?? []),
      ];

      return (
        <span
          className="inline-flex items-center gap-1 align-middle"
          data-tooltip-id={APP_TOOLTIP_ID}
          data-tooltip-html={
            tooltipLineList.length > 0
              ? renderToStaticMarkup(<TooltipLines lines={tooltipLineList} />)
              : undefined
          }
        >
          {isTemporal ? (
            <React.Fragment>
              <Clock aria-hidden="true" className={CELL_ICON_CLASS} />
              <span className="sr-only">{temporalLabel}</span>
            </React.Fragment>
          ) : null}
          {isGeoblocked ? (
            <React.Fragment>
              <EarthLock aria-hidden="true" className={CELL_ICON_CLASS} />
              <span className="sr-only">{geoblockLabel}</span>
            </React.Fragment>
          ) : null}
        </span>
      );
    },
  };
}
